import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const AdminDisputes = () => {
  const { toast } = useToast();
  const { data: disputes, isLoading } = useQuery({
    queryKey: ['admin-disputes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cleanliness_reports')
        .select(`
          *,
          reporter:profiles!cleanliness_reports_reporter_id_fkey (
            first_name,
            last_name
          ),
          cook:profiles!cleanliness_reports_cook_id_fkey (
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const handleStatusUpdate = async (reportId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('cleanliness_reports')
        .update({ 
          status: newStatus,
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', reportId);

      if (error) throw error;

      toast({
        title: "Status updated",
        description: `Report status has been set to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating report status:', error);
      toast({
        title: "Error",
        description: "Failed to update report status",
        variant: "destructive",
      });
    }
  };

  return (
    <AdminLayout title="Disputes">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          disputes?.map((dispute) => (
            <Card key={dispute.id} className="p-4">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium">Report from {dispute.reporter?.first_name} {dispute.reporter?.last_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Against {dispute.cook?.first_name} {dispute.cook?.last_name}
                  </p>
                </div>
                
                <p className="text-sm">{dispute.description}</p>
                
                <div className="flex gap-2">
                  {dispute.status === 'pending' && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => handleStatusUpdate(dispute.id, 'rejected')}
                      >
                        Reject
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(dispute.id, 'resolved')}
                      >
                        Resolve
                      </Button>
                    </>
                  )}
                  {dispute.status !== 'pending' && (
                    <span className="text-sm text-muted-foreground">
                      {dispute.status.charAt(0).toUpperCase() + dispute.status.slice(1)} 
                      {dispute.resolved_at && ` on ${new Date(dispute.resolved_at).toLocaleDateString()}`}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </AdminLayout>
  );
};